import { AdService } from '../..'
import { IAdDeviceStickyConfiguration, IAdStickyConfiguration } from '../../../../domain/conf/ads'
import { DOMEvents, DOMUtils } from '../../../../utils/dom'
import { AnalyticsService } from '../../../analytics'
import { PageType } from '../../../common/page-types'
import { AdEvents } from '../events'

export class StickyAd {
    public static readonly secToMsec = 1000
    public static activeTop = false
    public static enabled = false
    public static adClosed = false
    public static readonly placeholderId = 'ad-placeholder'
    public static readonly maxMobileHeight = 50
    public static readonly maxDesktopHeight = 90
    public static readonly adDivMargin = 16
    private static closeTimer = 0
    private static adMountElement: Element
    private static rightRailOffset = 8

    /**
     * Initializes the sticky mobile ad with config settings
     * @param conf The config object holding settings for the mobile sticky ad
     * @param pageType The type of the current page
     * @param count How many times the init function has been called (if page loads out of order)
     * @returns
     */
    public static init(confSettings: IAdStickyConfiguration, pageType: PageType, count = 0) {
        if (!confSettings) {
            return
        }
        const conf: IAdDeviceStickyConfiguration = DOMUtils.isMobile() ? confSettings.mobile : confSettings.desktop
        // Set default
        this.updateRightRailPosition()
        if (!conf || !conf.enabled) {
            return
        }
        conf.bottom = !!conf.bottom
        conf.close = !!conf.close
        conf.closeTimer = conf.closeTimer || 0
        conf.pageTargeting = conf.pageTargeting || []

        // Check sticky is enabled on current page type
        if (conf.pageTargeting.length > 0 && !conf.pageTargeting.includes(pageType)) {
            return
        }

        const topAd = document.querySelector(`.${AdService.DOM.classes.topAd.join('.')}`) as HTMLElement
        this.adMountElement = conf.bottom
            ? document.querySelector(`.${AdService.DOM.classes.fixedBottomAdContainer}`)
            : document.querySelector(`.${AdService.DOM.classes.header}`)

        if (this.adMountElement && topAd) {
            this.activeTop = !conf.bottom
            this.enabled = true
            // restrict ad height on desktop
            if (!DOMUtils.isMobile()) {
                this.setDesktopSizes()
            }
            // top ad placement
            if (!conf.bottom) {
                topAd.classList.add(AdService.DOM.classes.stickyAd)
                if (DOMUtils.isMobile()) {
                    this.adMountElement.append(topAd)
                    topAd.classList.add(AdService.DOM.classes.headerOffset)
                    topAd.setAttribute(AdService.DOM.attrs.dataScroll, AdService.DOM.vals.dataScroll)

                    // Make sticky top ad play nicely with different mobile headers
                    if (document.getElementsByClassName(AdService.DOM.classes.subscribe).length > 0) {
                        if (document.getElementsByClassName(AdService.DOM.classes.breaking).length > 0) {
                            topAd.classList.add(AdService.DOM.classes.stickyWithSubscribe)
                        } else {
                            topAd.classList.add(AdService.DOM.classes.stickyWithSubscribeNoBreaking)
                        }

                        // In case Subscribe button is removed
                        const observer = new MutationObserver((mutations, obs) => {
                            mutations.forEach(() => {
                                const subscribeEl = document.querySelector(`.${AdService.DOM.classes.subscribe}`)
                                if (!this.adMountElement.contains(subscribeEl)) {
                                    topAd.classList.remove(AdService.DOM.classes.stickyWithSubscribe)
                                    topAd.classList.remove(AdService.DOM.classes.stickyWithSubscribeNoBreaking)
                                    obs.disconnect() // Stop observing after the element is removed
                                }
                            })
                        })
                        observer.observe(this.adMountElement, { childList: true, subtree: true })
                    } else {
                        topAd.classList.add(AdService.DOM.classes.stickyNoSubscribe)
                    }
                } else {
                    window.addEventListener('scroll', () => {
                        this.stickyHeaderDesktop()
                    })
                    const containingDiv = document.createElement('div')
                    const placeholder = document.createElement('div')
                    placeholder.id = this.placeholderId
                    // topAd.parentElement.insertBefore(placeholder, topAd)
                    topAd.parentElement.insertBefore(containingDiv, topAd)
                    containingDiv.append(placeholder)
                    containingDiv.append(topAd)
                    topAd.style.position = 'relative'
                    topAd.style.width = '100%'
                    topAd.style.zIndex = '249' // 1 less than dropdown menu z-index
                }
                const adHeight = topAd.getBoundingClientRect().height
                this.updateRightRailPosition(adHeight)
                // bottom ad placement
            } else {
                this.adMountElement.append(topAd)
                const fixedBottomAddOffset = document.querySelector(
                    `#${AdService.DOM.ids.fixedBottomAddOffset}`
                ) as HTMLElement
                fixedBottomAddOffset.removeAttribute('hidden')
                fixedBottomAddOffset.style.height = `${
                    (DOMUtils.isMobile() ? this.maxMobileHeight : this.maxDesktopHeight) + this.adDivMargin
                }px`
            }

            if (conf.close) {
                this.closeTimer = conf.closeTimer || 0
                this.enableCloseButton(topAd)
            }
            // tslint:disable-next-line: no-magic-numbers
        } else if (count < 5) {
            // required for fast connections, which may load FEM before the top ad HTML tag
            // using event listeners for page load will not work for slow connections
            setTimeout(() => {
                this.init(confSettings, pageType, count + 1)
                // tslint:disable-next-line: no-magic-numbers
            }, 50)
        }
    }

    /**
     * Displays the ad close button, after a (possibly zero) time delay
     * @param topAd The div containing the top-of-page banner ad
     */
    public static enableCloseButton(topAd: Element) {
        const closeButton = topAd.querySelector(`.${AdService.DOM.classes.closeStickyAd}`) as HTMLElement

        if (closeButton) {
            if (this.closeTimer > 0) {
                setTimeout(() => {
                    closeButton.style.display = 'inline'
                }, this.closeTimer * this.secToMsec)
            } else {
                closeButton.style.display = 'inline'
            }
            closeButton.style.backgroundColor = 'rgb(247, 247, 247)'
            const oldTime = Date.now()

            closeButton.addEventListener('click', () => {
                // Immediate actions that directly affect user interaction should remain outside setTimeout
                topAd.remove()
                this.updateRightRailPosition()
                this.adClosed = true

                setTimeout(() => {
                    // Operations inside this block will be executed after the main thread is clear
                    const fixedBottomAddOffset = document.querySelector(
                        `#${AdService.DOM.ids.fixedBottomAddOffset}`
                    ) as HTMLElement
                    if (fixedBottomAddOffset) {
                        fixedBottomAddOffset.setAttribute('hidden', '')
                    }

                    const placeholder = document.getElementById(this.placeholderId)
                    if (placeholder) {
                        placeholder.setAttribute('hidden', '')
                    }

                    document.dispatchEvent(
                        new CustomEvent(AdEvents.STICKY_AD_CLOSED, {
                            detail: {
                                position: this.activeTop ? 'top' : 'bottom',
                            },
                        })
                    )

                    this.sendLoginAnalytics(Date.now() - oldTime)
                }, 0)
            })
        }
    }

    /**
     * Dispatches an mParticle event to track when users are clicking the sticky ad close button.
     * time_before_close_click tracks the time between the ad slot loading and the close button being clicked.
     * Note that this is the ad slot loading, not the ad creative loading. All times are in milliseconds.
     * show_close_button_delay is the delay between slot load and the close button being shown.
     * Total time the close button was visible is equal to time_before_close_click - show_close_button_delay.
     * user_agent tracks the user's browser and device types.
     * @param timeBeforeClick The time between ad slot loading and the close button being clicked
     */
    public static sendLoginAnalytics(timeBeforeClick: number) {
        document.dispatchEvent(
            new CustomEvent(DOMEvents.CUSTOM_EVENT, {
                detail: {
                    control_fields: {
                        mparticle: {
                            keys: {
                                show_close_button_delay: 'show_close_button_delay',
                                sticky_ad_position: 'sticky_ad_position',
                                time_before_close_click: 'time_before_close_click',
                            },
                            mp_event_type: 'Other',
                        },
                    },
                    event_name: AnalyticsService.customEventNames.adClosed,
                    show_close_button_delay: this.closeTimer * this.secToMsec,
                    sticky_ad_position: this.activeTop ? 'top' : 'bottom',
                    time_before_close_click: timeBeforeClick,
                },
            })
        )
    }

    /**
     * @returns whether the sticky ad is currently running on the page
     */
    public static isActive() {
        return this.enabled && !this.adClosed
    }

    /**
     * @returns whether the sticky ad is currently running on the page in the top position
     */
    public static isActiveTop() {
        return this.isActive() && this.activeTop
    }

    /**
     * Sticky desktop ads should not exceed a max height of 90px
     */
    private static setDesktopSizes() {
        const topAd = document.getElementById(AdService.DOM.ids.loc1)
        const sizes = JSON.parse(topAd.getAttribute(AdService.DOM.attrs.adSizes))
        topAd.setAttribute(
            AdService.DOM.attrs.adSizes,
            JSON.stringify(sizes.filter((subArray: [number, number]) => subArray[1] <= this.maxDesktopHeight))
        )
    }

    /**
     * Set sticky desktop header ad to correct height, and add placeholder to maintain page scroll depth.
     */
    private static stickyHeaderDesktop() {
        const secondaryNav = document.getElementById(AdService.DOM.ids.secondaryNav)
        const header = document.getElementById(AdService.DOM.ids.header)
        const topAd = document.querySelector(`.${AdService.DOM.classes.topAd.join('.')}`) as HTMLElement

        if (!topAd || !secondaryNav) {
            // In case there's no ad. E.g. subscriber or secondaryNav does not exist.
            return
        }

        const placeholder = document.getElementById(this.placeholderId)

        // If secondary nav is hidden (width < 1200px) and scroll is at top of page (needed for UX when rubberbanding)
        // or if the secondary nav is visible on the screen, then the desktop header ad is in its normal position
        if (
            (window.getComputedStyle(secondaryNav).display === 'none' && document.documentElement.scrollTop === 0) ||
            secondaryNav.getBoundingClientRect().bottom >= header.getBoundingClientRect().bottom
        ) {
            topAd.style.position = 'relative'
            topAd.style.top = 'auto'
            placeholder.style.height = ''
        } else {
            topAd.style.position = 'fixed'
            topAd.style.top = `${header.getBoundingClientRect().bottom}px`
            placeholder.style.height = `${topAd.getBoundingClientRect().height}px`
        }
    }

    private static updateRightRailPosition(offset = 0) {
        const rightRails = document.querySelectorAll(`.${AdService.DOM.classes.rightRailItem}`)

        const headerHeight = document.getElementById(AdService.DOM.ids.header).getBoundingClientRect().height
        rightRails.forEach((rail: HTMLElement) => {
            rail.style.top = `${headerHeight + offset + this.rightRailOffset}px`
        })
    }
}
